import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircleCheck, CircleX, Info, TriangleAlert } from 'lucide-react';
import { registerNotify } from '../utils/notify';
import { useLanguage } from '../contexts/LanguageContext';
import styles from './Toaster.module.css';

// Errors stay longer: they usually need reading.
const DURATION_MS = { success: 4000, info: 5000, warning: 7000, error: 8000 };
const MAX_TOASTS = 4;
// One icon per type: the type shows by more than its colour.
const ICONS = { success: CircleCheck, error: CircleX, warning: TriangleAlert, info: Info };

/**
 * Non-blocking messages for notify() (utils/notify.js): while this is
 * mounted, notify() and reportLoadError() show a toast instead of alert().
 */
const Toaster = () => {
  const { t } = useLanguage();
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((list) => list.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((message, type) => {
    const id = nextId.current++;
    const kind = DURATION_MS[type] ? type : 'info';
    setToasts((list) => [...list, { id, message, type: kind }].slice(-MAX_TOASTS));
    timers.current.set(id, setTimeout(() => dismiss(id), DURATION_MS[kind]));
  }, [dismiss]);

  useEffect(() => {
    registerNotify(show);
    const pending = timers.current;
    return () => {
      registerNotify(null);
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, [show]);

  return (
    <div className={styles.region} aria-live="polite" aria-relevant="additions">
      {toasts.map((toast) => {
        const Icon = ICONS[toast.type];
        return (
        <div key={toast.id} className={`${styles.toast} ${styles[toast.type]}`} role={toast.type === 'error' ? 'alert' : 'status'}>
          <Icon className={styles.icon} aria-hidden="true" />
          <span className={styles.message}>{toast.message}</span>
          <button type="button" className={styles.close} onClick={() => dismiss(toast.id)} aria-label={t('Close')}>
            ×
          </button>
        </div>
        );
      })}
    </div>
  );
};

export default Toaster;
